import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api.js';

type Group = { dimension: string; values: Array<{ value: string; count: number }> };
type Data = { teams: Array<{ id: string; name: string }>; dimensions: Group[] };
type Preview = Awaited<ReturnType<typeof api.managerGeographyPreview>>;

function message(error: unknown): string {
  if (error instanceof ApiError && error.reason === 'TEAM_EXISTS') return 'A team with that name already exists.';
  if (error instanceof ApiError && error.status === 403) return 'Your Manager access does not cover this action.';
  return 'The change could not be saved. Try again.';
}

export function TeamAssignmentsScreen({ onOpenRoutes, onManualTrip, onOpenMap, onReviewAddresses }: {
  onOpenRoutes: () => void; onManualTrip: () => void; onOpenMap: () => void;
  onReviewAddresses: () => void;
}) {
  const [step, setStep] = useState<'setup' | 'clusters' | 'routes'>('setup');
  const [data, setData] = useState<Data | null>(null);
  const [stateData, setStateData] = useState<Awaited<ReturnType<typeof api.stateTeams>> | null>(null);
  const [selectedState, setSelectedState] = useState('');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [baseTeamId, setBaseTeamId] = useState('');
  const [baseLabel, setBaseLabel] = useState('');
  const [baseLat, setBaseLat] = useState('');
  const [baseLng, setBaseLng] = useState('');
  const [routeDate, setRouteDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [routeClassification, setRouteClassification] = useState<'LOGISTICS' | 'PROTECTED'>('LOGISTICS');
  const [clusterPreview, setClusterPreview] = useState<Awaited<ReturnType<typeof api.clusterPreview>> | null>(null);
  const [clusterTeamIds, setClusterTeamIds] = useState<Record<string, string[]>>({});
  const [preview, setPreview] = useState<Preview | null>(null);
  const [dimension, setDimension] = useState('cluster');
  const [value, setValue] = useState('');
  const [teamId, setTeamId] = useState('');
  const [newTeam, setNewTeam] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void api.managerGeography().then(setData).catch((cause) => setError(message(cause)));
    void api.stateTeams().then(setStateData).catch((cause) => setError(message(cause)));
  }, []);

  useEffect(() => {
    setSelectedTeamIds(stateData?.assignments.filter((item) => item.state === selectedState)
      .map((item) => item.team_id) ?? []);
  }, [selectedState, stateData]);

  useEffect(() => { setClusterPreview(null); setClusterTeamIds({}); },
    [selectedState, routeClassification]);

  useEffect(() => {
    const team = stateData?.teams.find((item) => item.id === baseTeamId);
    setBaseLabel(team?.base_label ?? '');
    setBaseLat(team?.base_lat === null || team?.base_lat === undefined
      ? '' : String(team.base_lat));
    setBaseLng(team?.base_lng === null || team?.base_lng === undefined
      ? '' : String(team.base_lng));
  }, [baseTeamId, stateData]);

  const values = data?.dimensions.find((item) => item.dimension === dimension)?.values ?? [];

  useEffect(() => {
    if (!value) { setPreview(null); return; }
    let current = true;
    setPreview(null);
    void api.managerGeographyPreview(dimension, value)
      .then((result) => { if (current) setPreview(result); })
      .catch((cause) => { if (current) setError(message(cause)); });
    return () => { current = false; };
  }, [dimension, value]);

  async function createTeam(event: React.FormEvent) {
    event.preventDefault();
    if (!newTeam.trim() || busy) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const { team } = await api.managerCreateTeam(newTeam.trim());
      setData(await api.managerGeography());
      setStateData(await api.stateTeams());
      setNewTeam('');
      setTeamId(team.id);
      setNotice(`${team.name} created. Select a geographic group to assign it.`);
    } catch (cause) { setError(message(cause)); }
    finally { setBusy(false); }
  }

  async function assign(event: React.FormEvent) {
    event.preventDefault();
    if (!value || busy || !preview || preview.routedStops > 0) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const result = await api.managerAssignGeography({
        dimension, value, teamId: teamId || null, fingerprint: preview.fingerprint,
      });
      setPreview(await api.managerGeographyPreview(dimension, value));
      setNotice(`${result.updated} record${result.updated === 1 ? '' : 's'} updated.`);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 409) {
        setError(cause.reason === 'assignment_preview_stale'
          ? 'This group changed since the preview. Review it again before applying.'
          : 'An existing trip uses this group. Review that route before changing its team.');
        void api.managerGeographyPreview(dimension, value)
          .then(setPreview).catch(() => setPreview(null));
      } else setError(message(cause));
    }
    finally { setBusy(false); }
  }

  async function saveStateTeams(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedState || busy) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      await api.setStateTeams(selectedState, selectedTeamIds);
      setStateData(await api.stateTeams());
      setNotice(`Team coverage for ${selectedState} updated. Existing trips were not changed.`);
    } catch (cause) { setError(message(cause)); }
    finally { setBusy(false); }
  }

  async function saveTeamBase(event: React.FormEvent) {
    event.preventDefault();
    const lat = Number(baseLat);
    const lng = Number(baseLng);
    if (!baseTeamId || !baseLabel.trim() || !baseLat.trim() || !baseLng.trim()
      || !Number.isFinite(lat) || !Number.isFinite(lng) || busy) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      await api.setTeamBase(baseTeamId, baseLabel.trim(), lat, lng);
      setStateData(await api.stateTeams());
      setNotice('Team base saved. Future recommendations will use it when no route is active.');
    } catch (cause) { setError(message(cause)); }
    finally { setBusy(false); }
  }

  async function previewStateClusters(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedState || busy) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const result = await api.clusterPreview(selectedState, routeClassification);
      if (result.error) throw new Error(result.error);
      setClusterPreview(result);
      setClusterTeamIds({});
      setNotice(`${result.clusters.length} geographic clusters are ready for review. No trips were created.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : message(cause));
    } finally { setBusy(false); }
  }

  async function planCluster(fingerprint: string) {
    if (!clusterPreview || busy) return;
    const teams = clusterTeamIds[fingerprint] ?? [];
    if (!teams.length) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const result = await api.planReviewedCluster(selectedState, routeClassification,
        routeDate, fingerprint, teams);
      if (result.error) throw new Error(result.message ?? result.error);
      setClusterPreview(await api.clusterPreview(selectedState, routeClassification));
      setNotice(`${result.stopCount ?? 0} reviewed stops planned across ${result.teamCount ?? 0} teams. ${result.estimated ? 'Travel is estimated.' : 'Road distances were used where available.'}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : message(cause));
      if (cause instanceof Error && cause.message === 'cluster_preview_stale') {
        setClusterPreview(null);
      }
    } finally { setBusy(false); }
  }

  return <section className="pt-4 desktop:pt-6 max-w-[980px] space-y-4">
    <div>
      <h1>Planning</h1>
      <p className="mt-1 text-sm text-neutral-700">
        Set coverage, review geographic clusters, then manage the resulting routes. Planning changes are for managers and administrators.
      </p>
    </div>
    <nav className="grid gap-2 desktop:grid-cols-3" aria-label="Planning steps">
      {([
        ['setup', '1. Teams & coverage'],
        ['clusters', '2. Cluster & plan'],
        ['routes', '3. Routes & changes'],
      ] as const).map(([id, label]) => <button key={id} type="button"
        className={step === id ? 'btn btn-primary' : 'btn btn-secondary'}
        aria-current={step === id ? 'step' : undefined}
        onClick={() => setStep(id)}>{label}</button>)}
    </nav>
    {stateData && <div className="grid gap-2 desktop:grid-cols-3" aria-label="Planning readiness">
      <p className="card text-sm"><strong>{stateData.teams.length}</strong> teams, {stateData.teams.filter((team) => team.base_lat !== null).length} with bases</p>
      <p className="card text-sm"><strong>{new Set(stateData.assignments.map((item) => item.state)).size}</strong> states with team coverage</p>
      <p className="card text-sm"><strong>{clusterPreview?.clusters.length ?? 0}</strong> current cluster drafts</p>
    </div>}
    {error && <p role="alert" className="form-error">{error}</p>}
    {notice && <p role="status" className="text-sm text-accent-800">{notice}</p>}
    {!data ? <p role="status">Loading geographic groups...</p> : <>
      {step === 'setup' && <>
      <p className="text-sm text-neutral-700">Create each team, add its usual starting point, and choose which states it covers. These choices do not change an existing trip.</p>
      <form className="card border-accent-300" onSubmit={(event) => void createTeam(event)}>
        <h2 className="text-xl">Create a team</h2>
        <div className="desktop:flex desktop:items-end desktop:gap-3 mt-4">
          <label className="field flex-1 mb-0" htmlFor="manager-new-team"><span>Team name</span>
            <input id="manager-new-team" className="input" maxLength={80} value={newTeam}
              placeholder="Example: Central Team" onChange={(event) => setNewTeam(event.target.value)} />
          </label>
          <button className="btn btn-secondary mt-3 desktop:mt-0" type="submit"
            disabled={busy || !newTeam.trim()}>{busy ? 'Saving...' : 'Create team'}</button>
        </div>
      </form>
      <form className="card border-accent-300" onSubmit={(event) => void saveTeamBase(event)}>
        <h2 className="text-xl">Team starting locations</h2>
        <p className="text-sm text-neutral-700 mt-1">Set each team’s usual starting point for future AV assignment recommendations. Existing trips are unchanged.</p>
        <label className="field mt-4" htmlFor="base-team"><span>Team</span>
          <select id="base-team" className="input" value={baseTeamId}
            onChange={(event) => setBaseTeamId(event.target.value)}>
            <option value="">Select a team</option>
            {stateData?.teams.map((team) => <option key={team.id} value={team.id}>
              {team.name}{team.base_lat === null ? ' (base needed)' : ''}
            </option>)}
          </select>
        </label>
        <label className="field mt-3" htmlFor="base-label"><span>Base name</span>
          <input id="base-label" className="input" maxLength={120} required
            placeholder="Office or staging point" value={baseLabel}
            onChange={(event) => setBaseLabel(event.target.value)} />
        </label>
        <div className="grid gap-3 mt-3 desktop:grid-cols-2">
          <label className="field" htmlFor="base-lat"><span>Latitude</span>
            <input id="base-lat" className="input" type="number" step="any" min={-90} max={90}
              required value={baseLat} onChange={(event) => setBaseLat(event.target.value)} />
          </label>
          <label className="field" htmlFor="base-lng"><span>Longitude</span>
            <input id="base-lng" className="input" type="number" step="any" min={-180} max={180}
              required value={baseLng} onChange={(event) => setBaseLng(event.target.value)} />
          </label>
        </div>
        <p className="text-xs text-neutral-600 mt-2">Copy the coordinates from a map pin for the team’s usual starting point.</p>
        <button className="btn btn-primary mt-4" disabled={!baseTeamId || busy}>Save team base</button>
      </form>
      <form className="card border-accent-300" onSubmit={(event) => void saveStateTeams(event)}>
        <h2 className="text-xl">Teams assigned to a state</h2>
        <p className="text-sm text-neutral-700 mt-1">Select every team available for new routes in that state. Existing trips stay as planned.</p>
        <label className="field mt-4" htmlFor="coverage-state"><span>State</span>
          <select id="coverage-state" className="input" value={selectedState}
            onChange={(event) => setSelectedState(event.target.value)}>
            <option value="">Select a state</option>
            {stateData?.states.map((item) => <option key={item.state} value={item.state}>
              {item.state} ({item.people} people)
            </option>)}
          </select>
        </label>
        {selectedState && <div className="mt-4 grid gap-2 desktop:grid-cols-2">
          {stateData?.teams.map((team) => <label key={team.id} className="flex items-center gap-2">
            <input type="checkbox" checked={selectedTeamIds.includes(team.id)}
              onChange={(event) => setSelectedTeamIds((current) => event.target.checked
                ? [...current, team.id] : current.filter((id) => id !== team.id))} />
            <span>{team.name}</span>
          </label>)}
        </div>}
        <button className="btn btn-primary mt-4" disabled={!selectedState || busy}>Save state teams</button>
      </form>
      </>}
      {step === 'clusters' && <>
      <p className="text-sm text-neutral-700">Choose a covered state and trip date. Preview first, then assign one or more teams to each cluster before scheduling it.</p>
      <label className="field" htmlFor="planning-state"><span>State to plan</span>
        <select id="planning-state" className="input" value={selectedState}
          onChange={(event) => setSelectedState(event.target.value)}>
          <option value="">Select a state</option>
          {stateData?.states.map((item) => <option key={item.state} value={item.state}>
            {item.state} ({item.people} people)
          </option>)}
        </select>
      </label>
      <form className="card border-accent-300" onSubmit={(event) => void previewStateClusters(event)}>
        <h2 className="text-xl">1. Cluster locations before planning</h2>
        <p className="text-sm text-neutral-700 mt-1">Nearest-neighbor grouping creates drafts of at most 20 physical stops, leaving room for drive time in a team day. Review each group and choose its teams before creating a trip.</p>
        <label className="field mt-4" htmlFor="state-route-date"><span>Trip date</span>
          <input id="state-route-date" className="input" type="date" required value={routeDate}
            onChange={(event) => setRouteDate(event.target.value)} />
        </label>
        <label className="field mt-3" htmlFor="state-route-classification"><span>Route classification</span>
          <select id="state-route-classification" className="input" value={routeClassification}
            onChange={(event) => setRouteClassification(event.target.value as 'LOGISTICS' | 'PROTECTED')}>
            <option value="LOGISTICS">Standard</option>
            <option value="PROTECTED">Protected, no external navigation</option>
          </select>
        </label>
        <button className="btn btn-primary mt-4" disabled={!selectedState || busy}>
          Generate {selectedState || 'state'} cluster drafts
        </button>
      </form>
      {clusterPreview && <section className="card border-accent-300 space-y-4" aria-label="Reviewed geographic clusters">
        <h2 className="text-xl">2. Assign teams, then plan each cluster</h2>
        <p className="text-sm text-neutral-700">{clusterPreview.clusters.length} drafts, {clusterPreview.capacity} stops maximum each. Generating drafts did not schedule visits.</p>
        {clusterPreview.heldUnverifiedStops > 0 && <p role="status" className="text-sm text-amber-800">
          {clusterPreview.heldUnverifiedStops} unverified location{clusterPreview.heldUnverifiedStops === 1 ? '' : 's'} held for address review. Teams should not visit them.
        </p>}
        {clusterPreview.clusters.length === 0 && <p>No route-ready stops were found for this state and classification.</p>}
        {clusterPreview.clusters.map((cluster) => <div key={cluster.fingerprint} className="rounded border p-4 space-y-2">
          <h3 className="font-semibold">Cluster {cluster.ordinal}: {cluster.stopCount} stops, {cluster.visitCount} visits</h3>
          <p className="text-sm">{cluster.places.join(', ')}</p>
          <div className="grid gap-2 desktop:grid-cols-3" aria-label={`Teams for cluster ${cluster.ordinal}`}>
            {clusterPreview.teams.map((team) => <label key={team.id} className="flex gap-2 items-center">
              <input type="checkbox" checked={(clusterTeamIds[cluster.fingerprint] ?? []).includes(team.id)}
                onChange={(event) => setClusterTeamIds((current) => {
                  const selected = current[cluster.fingerprint] ?? [];
                  return { ...current, [cluster.fingerprint]: event.target.checked
                    ? [...selected, team.id] : selected.filter((id) => id !== team.id) };
                })} />
              <span>{team.name}</span>
            </label>)}
          </div>
          <button type="button" className="btn btn-primary" disabled={busy || !(clusterTeamIds[cluster.fingerprint] ?? []).length}
            onClick={() => void planCluster(cluster.fingerprint)}>Plan reviewed cluster</button>
        </div>)}
      </section>}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn btn-secondary" onClick={onManualTrip}>Plan a trip manually</button>
        <button type="button" className="btn btn-secondary" onClick={onReviewAddresses}>Review unverified addresses</button>
        <button type="button" className="btn btn-secondary" onClick={() => setStep('routes')}>Review routes</button>
      </div>
      </>}
      {step === 'setup' && <>
      <details className="card border-accent-300">
        <summary className="cursor-pointer font-semibold">Advanced: assign an existing geographic group</summary>
      <form className="card border-accent-300" onSubmit={(event) => void assign(event)}>
        <h2 className="text-xl">Geographic team assignment</h2>
        <p className="text-sm text-neutral-700 mt-1">
          Records remain unassigned until you apply a team. Choose Not assigned to clear a group.
        </p>
        <div className="mt-5 grid gap-4 desktop:grid-cols-3">
          <label className="field" htmlFor="manager-dimension"><span>Group records by</span>
            <select id="manager-dimension" className="input" value={dimension}
              onChange={(event) => { setDimension(event.target.value); setValue(''); }}>
              {data.dimensions.map((item) => <option key={item.dimension} value={item.dimension}>
                {item.dimension === 'zip' ? 'ZIP code' : item.dimension[0].toUpperCase() + item.dimension.slice(1)}
              </option>)}
            </select>
          </label>
          <label className="field" htmlFor="manager-value"><span>Geographic value</span>
            <select id="manager-value" className="input" value={value} required
              onChange={(event) => setValue(event.target.value)}>
              <option value="">Select a value</option>
              {values.map((item) => <option key={item.value} value={item.value}>
                {item.value} ({item.count})
              </option>)}
            </select>
          </label>
          <label className="field" htmlFor="manager-team"><span>Team</span>
            <select id="manager-team" className="input" value={teamId}
              onChange={(event) => setTeamId(event.target.value)}>
              <option value="">Not assigned</option>
              {data.teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
            </select>
          </label>
        </div>
        {preview && <div className="mt-5 rounded border p-4" aria-label="Assignment preview">
          <h3 className="font-semibold">Review before applying</h3>
          <p className="text-sm mt-1">{preview.people} people at {preview.stops.length} stops.
            {preview.unresolvedStops > 0 && ` ${preview.unresolvedStops} address${preview.unresolvedStops === 1 ? '' : 'es'} still need location review.`}
          </p>
          {preview.routedStops > 0 && <p role="alert" className="text-sm mt-2">
            {preview.routedStops} stop{preview.routedStops === 1 ? '' : 's'} already belong to a trip. This assignment will not silently change an active route.
          </p>}
          <ul className="mt-3 text-sm max-h-48 overflow-y-auto">
            {preview.stops.map((stop) => <li key={stop.stop_id} className="py-1 border-t">
              {stop.address} · {stop.people} {stop.people === 1 ? 'person' : 'people'}
              {stop.current_teams.length > 0 && ` · ${stop.current_teams.join(', ')}`}
              {stop.unresolved && ' · Unresolved'}{stop.routed && ' · On a trip'}
            </li>)}
          </ul>
        </div>}
        <button className="btn btn-primary mt-5" disabled={busy || !preview || preview.routedStops > 0 || preview.people === 0} type="submit">
          {busy ? 'Applying...' : 'Apply assignment'}
        </button>
      </form>
      </details>
      </>}
      {step === 'routes' && <section className="card border-accent-300 space-y-3">
        <h2 className="text-xl">Review and adjust routes</h2>
        <p className="text-sm text-neutral-700">Open the current team schedule to reorder stops or move them between routes. The map shows where planned stops fall.</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn btn-primary" onClick={onOpenRoutes}>Open team routes</button>
          <button type="button" className="btn btn-secondary" onClick={onOpenMap}>Open route map</button>
        </div>
        <p className="text-sm text-neutral-700">Individual case transfers happen on the person’s case page. Select the case first, then choose the receiving team.</p>
      </section>}
    </>}
  </section>;
}
